import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient, User, Session } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from '../utils/supabase/info';

// Supabase client configuration
const supabaseUrl = `https://${projectId}.supabase.co`;
const supabaseAnonKey = publicAnonKey;

// Create Supabase client
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Auth context types
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error?: { message?: string } | null; data?: unknown }>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
}

// Create auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);

  const clearSupabaseStorage = () => {
    // Only clear Supabase-related keys (avoid nuking unrelated app state).
    try {
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem(`sb-${projectId}-auth-token`);
      sessionStorage.removeItem('supabase.auth.token');
      sessionStorage.removeItem(`sb-${projectId}-auth-token`);

      for (const key of Object.keys(localStorage)) {
        if (key === `sb-${projectId}-auth-token` || key.startsWith(`sb-${projectId}-`)) {
          localStorage.removeItem(key);
        }
      }
      for (const key of Object.keys(sessionStorage)) {
        if (key === `sb-${projectId}-auth-token` || key.startsWith(`sb-${projectId}-`)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('🔴 Supabase Auth: Error clearing storage:', error);
    }
  };

  useEffect(() => {
    // Get initial session quickly without showing loading state
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('🔴 Supabase Auth: Error getting session:', error);
          // Handle refresh token errors
          if (error.message?.includes('Invalid Refresh Token') || error.message?.includes('Refresh Token Not Found')) {
            console.log('🔧 Supabase Auth: Clearing invalid session due to refresh token error');
            clearSupabaseStorage();
            // Best-effort server sign-out to invalidate any stale session.
            await supabase.auth.signOut({ scope: 'global' });
            setSession(null);
            setUser(null);
          }
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          console.log('🔐 Supabase Auth: Initial session loaded:', session ? 'authenticated' : 'not authenticated');
        }
      } catch (error) {
        console.error('🔴 Supabase Auth: Error in getInitialSession:', error);
        // Handle potential refresh token errors in catch block too
        if (error instanceof Error && (error.message?.includes('Invalid Refresh Token') || error.message?.includes('Refresh Token Not Found'))) {
          console.log('🔧 Supabase Auth: Clearing invalid session due to refresh token error (caught)');
          clearSupabaseStorage();
          await supabase.auth.signOut({ scope: 'global' });
          setSession(null);
          setUser(null);
        }
      }
      // No loading state needed - go straight to UI
    };

    getInitialSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Supabase Auth: State change:', event, session ? 'authenticated' : 'not authenticated');
        
        // Handle refresh token errors during auth state changes
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.log('🔧 Supabase Auth: Token refresh failed, clearing session');
          clearSupabaseStorage();
          await supabase.auth.signOut({ scope: 'global' });
          setSession(null);
          setUser(null);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }
        // No loading state management needed for faster UX
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Sign in function
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log('🔐 Supabase Auth: Attempting sign in for:', email);

      // Clear any potentially stale local tokens before sign-in.
      // Local-only cleanup (avoid server/global signOut here to prevent auth event races).
      clearSupabaseStorage();
      setSession(null);
      setUser(null);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('🔴 Supabase Auth: Sign in error:', error);
        
        // Handle refresh token errors during sign in
        if (error.message?.includes('Invalid Refresh Token') || error.message?.includes('Refresh Token Not Found')) {
          console.log('🔧 Supabase Auth: Refresh token error during sign in, clearing session');
          clearSupabaseStorage();
          await supabase.auth.signOut({ scope: 'global' });
          setSession(null);
          setUser(null);
        }
        
        return { error };
      }

      console.log('✅ Supabase Auth: Sign in successful:', data.user?.email);
      // Set state immediately to avoid relying on event ordering in production.
      setSession(data.session ?? null);
      setUser(data.user ?? null);
      return { data };
    } catch (error) {
      console.error('🔴 Supabase Auth: Sign in exception:', error);
      
      // Handle refresh token errors in catch block
      if (error instanceof Error && (error.message?.includes('Invalid Refresh Token') || error.message?.includes('Refresh Token Not Found'))) {
        console.log('🔧 Supabase Auth: Refresh token error during sign in (caught), clearing session');
        clearSupabaseStorage();
        await supabase.auth.signOut({ scope: 'global' });
        setSession(null);
        setUser(null);
      }
      
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      setLoading(true);
      console.log('🔐 Supabase Auth: Signing out...');
      
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      if (error) {
        console.error('🔴 Supabase Auth: Sign out error:', error);
      } else {
        console.log('✅ Supabase Auth: Sign out successful');
      }
      clearSupabaseStorage();
      setSession(null);
      setUser(null);
    } catch (error) {
      console.error('🔴 Supabase Auth: Sign out exception:', error);
      clearSupabaseStorage();
      setSession(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signIn,
    signOut,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Export the supabase client for direct use if needed
export { supabase };